import { useQuery } from '@tanstack/react-query'
import { useState, useEffect, useRef } from 'react'
import { dashboardConfigApi } from '@/lib/api'
import { BaseWidget } from './BaseWidget'
import { Timer, Play, Clock, BarChart } from 'lucide-react'
import type { TimeTrackingStatsData, ActiveTimerData, DailyTimeData, RecentTimeEntryData } from '@/types'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { BarChart as RechartsBarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts'

// Hook to get container dimensions using a callback ref
function useContainerDimensions() {
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null)
  const [element, setElement] = useState<HTMLDivElement | null>(null)

  const containerRef = useRef<HTMLDivElement | null>(null)
  const setRef = (node: HTMLDivElement | null) => {
    containerRef.current = node
    setElement(node)
  }

  useEffect(() => {
    if (!element) return

    let rafId: number
    let timeoutId: ReturnType<typeof setTimeout>

    const checkDimensions = () => {
      if (element) {
        const { offsetWidth, offsetHeight } = element
        if (offsetWidth > 0 && offsetHeight > 0) {
          setDimensions({ width: offsetWidth, height: offsetHeight })
        } else {
          timeoutId = setTimeout(() => {
            rafId = requestAnimationFrame(checkDimensions)
          }, 50)
        }
      }
    }

    checkDimensions()

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect
        if (width > 0 && height > 0) {
          setDimensions({ width: Math.floor(width), height: Math.floor(height) })
        }
      }
    })

    resizeObserver.observe(element)

    return () => {
      cancelAnimationFrame(rafId)
      clearTimeout(timeoutId)
      resizeObserver.disconnect()
    }
  }, [element])

  return { containerRef: setRef, dimensions }
}

// Time Tracking Stats Widget
export function TimeTrackingStatsWidget() {
  const { t } = useTranslation()
  const { data, isLoading } = useQuery({
    queryKey: ['widget', 'TIME_TRACKING_STATS'],
    queryFn: () => dashboardConfigApi.getWidgetData<TimeTrackingStatsData>('TIME_TRACKING_STATS'),
  })

  const stats = data?.data

  return (
    <BaseWidget
      title={t('dashboard.timeTrackingStats')}
      icon={<Timer className="h-4 w-4 text-muted-foreground" />}
      isLoading={isLoading}
    >
      <div className="grid grid-cols-3 gap-4">
        <div className="text-center">
          <p className="text-2xl font-bold">{stats?.total_hours_today?.toFixed(1) || '0.0'}h</p>
          <p className="text-xs text-muted-foreground">{t('timeTracking.today')}</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold">{stats?.total_hours_week?.toFixed(1) || '0.0'}h</p>
          <p className="text-xs text-muted-foreground">{t('timeTracking.thisWeek')}</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-green-500">{stats?.billable_hours_week?.toFixed(1) || '0.0'}h</p>
          <p className="text-xs text-muted-foreground">{t('timeTracking.billable')}</p>
        </div>
      </div>
    </BaseWidget>
  )
}

// Active Timer Widget
export function ActiveTimerWidget() {
  const { t } = useTranslation()
  const { data, isLoading } = useQuery({
    queryKey: ['widget', 'ACTIVE_TIMER'],
    queryFn: () => dashboardConfigApi.getWidgetData<ActiveTimerData | null>('ACTIVE_TIMER'),
    refetchInterval: 60000, // Refresh every minute
  })

  const timer = data?.data
  const [elapsed, setElapsed] = useState('')

  useEffect(() => {
    if (!timer?.start_time) {
      setElapsed('')
      return
    }

    const updateElapsed = () => {
      const start = new Date(timer.start_time).getTime()
      const now = Date.now()
      const diff = Math.floor((now - start) / 1000)
      const hours = Math.floor(diff / 3600)
      const minutes = Math.floor((diff % 3600) / 60)
      const seconds = diff % 60
      setElapsed(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`)
    }

    updateElapsed()
    const interval = setInterval(updateElapsed, 1000)
    return () => clearInterval(interval)
  }, [timer?.start_time])

  return (
    <BaseWidget
      title={t('dashboard.activeTimer')}
      icon={<Play className="h-4 w-4 text-green-500" />}
      isLoading={isLoading}
    >
      {timer ? (
        <div className="space-y-2">
          <div className="text-3xl font-mono font-bold text-center text-green-500">{elapsed}</div>
          <p className="text-sm text-center truncate">{timer.description || t('timeTracking.noDescription')}</p>
          {timer.client_name && (
            <p className="text-xs text-muted-foreground text-center">{timer.client_name}</p>
          )}
          <div className="flex justify-center">
            <Link to="/time-tracking">
              <Button size="sm" variant="outline">{t('timeTracking.viewTimer')}</Button>
            </Link>
          </div>
        </div>
      ) : (
        <div className="text-center py-4">
          <p className="text-sm text-muted-foreground mb-2">{t('timeTracking.noActiveTimer')}</p>
          <Link to="/time-tracking">
            <Button size="sm">{t('timeTracking.startTimer')}</Button>
          </Link>
        </div>
      )}
    </BaseWidget>
  )
}

// Weekly Time Chart Widget
export function WeeklyTimeChartWidget() {
  const { t } = useTranslation()
  const { containerRef, dimensions } = useContainerDimensions()
  const { data, isLoading } = useQuery({
    queryKey: ['widget', 'WEEKLY_TIME_CHART'],
    queryFn: () => dashboardConfigApi.getWidgetData<DailyTimeData[]>('WEEKLY_TIME_CHART'),
  })

  const chartData = data?.data?.map(d => ({
    day: new Date(d.date).toLocaleDateString('en-US', { weekday: 'short' }),
    hours: d.total_hours,
  })) || []

  const hasData = chartData.length > 0

  return (
    <BaseWidget
      title={t('dashboard.weeklyTimeChart')}
      icon={<BarChart className="h-4 w-4 text-muted-foreground" />}
      isLoading={isLoading}
    >
      <div ref={containerRef} className="w-full h-[150px]">
        {hasData && dimensions ? (
          <RechartsBarChart data={chartData} width={dimensions.width} height={dimensions.height}>
            <XAxis dataKey="day" fontSize={12} />
            <YAxis fontSize={12} />
            <Tooltip />
            <Bar dataKey="hours" fill="#3b82f6" name={t('timeTracking.hours')} />
          </RechartsBarChart>
        ) : !hasData && !isLoading ? (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">
            {t('common.noData')}
          </div>
        ) : null}
      </div>
    </BaseWidget>
  )
}

// Recent Time Entries Widget
export function RecentTimeEntriesWidget() {
  const { t } = useTranslation()
  const { data, isLoading } = useQuery({
    queryKey: ['widget', 'RECENT_TIME_ENTRIES'],
    queryFn: () => dashboardConfigApi.getWidgetData<RecentTimeEntryData[]>('RECENT_TIME_ENTRIES'),
  })

  const entries = data?.data || []

  const formatDuration = (minutes?: number) => {
    if (!minutes) return '-'
    const h = Math.floor(minutes / 60)
    const m = minutes % 60
    return `${h}h ${m}m`
  }

  return (
    <BaseWidget
      title={t('dashboard.recentTimeEntries')}
      icon={<Clock className="h-4 w-4 text-muted-foreground" />}
      isLoading={isLoading}
    >
      <div className="space-y-3">
        {entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('dashboard.noRecentTimeEntries')}</p>
        ) : (
          entries.map((entry) => (
            <div key={entry.entry_id} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{entry.description || t('timeTracking.noDescription')}</p>
                <p className="text-xs text-muted-foreground truncate">{entry.client_name || '-'}</p>
              </div>
              <div className="text-right ml-2">
                <p className="text-sm font-medium">{formatDuration(entry.duration_minutes)}</p>
                {entry.is_billable && (
                  <Badge variant="outline" className="text-xs">{t('timeTracking.billable')}</Badge>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </BaseWidget>
  )
}

